/**
 * Talk-o AI 同声传译页面
 *
 * 架构特点：
 * - 实时音频双向流传输
 * - 🌟 智能历史记录追踪：根据时间差和文本重合度，自动区分“更新当前句”和“追加新句子”
 * - 自动平滑滚动到底部
 */

import { useState, useRef, useCallback, useEffect } from 'react'

// =============================================================================
// 类型定义
// =============================================================================

interface TranscriptionMessage {
  type: 'transcription'
  input_transcription?: string
  output_transcription?: string
}

interface ErrorMessage {
  type: 'error'
  message: string
}

type TranslateWebSocketMessage = TranscriptionMessage | ErrorMessage

interface LanguageOption {
  code: string
  label: string
}

interface TransLine {
  id: string
  text: string
  lastUpdate: number
}

// =============================================================================
// 常量配置
// =============================================================================

const LANGUAGES: LanguageOption[] = [
  { code: 'en', label: '英语 (English)' },
  { code: 'ja', label: '日语 (日本語)' },
  { code: 'fr', label: '法语 (Français)' },
  { code: 'es', label: '西班牙语 (Español)' },
  { code: 'de', label: '德语 (Deutsch)' },
  { code: 'ko', label: '韩语 (한국어)' },
  { code: 'ru', label: '俄语 (Русский)' },
  { code: 'it', label: '意大利语 (Italiano)' },
]

const CAPTURE_SAMPLE_RATE = 16000
const PLAYBACK_SAMPLE_RATE = 24000
const CHUNK_INTERVAL_MS = 100

// =============================================================================
// 音频格式转换工具
// =============================================================================

function floatToInt16PCM(floatData: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(floatData.length * 2)
  const view = new DataView(buffer)
  for (let i = 0; i < floatData.length; i++) {
    let sample = Math.max(-1, Math.min(1, floatData[i]))
    sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff
    view.setInt16(i * 2, sample, true)
  }
  return buffer
}

function resampleFloat32(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input
  const ratio = fromRate / toRate
  const outputLength = Math.floor(input.length / ratio)
  const output = new Float32Array(outputLength)
  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i * ratio
    const index0 = Math.floor(srcIndex)
    const index1 = Math.min(index0 + 1, input.length - 1)
    const frac = srcIndex - index0
    output[i] = input[index0] * (1 - frac) + input[index1] * frac
  }
  return output
}

function createCaptureWorkletURL(): string {
  const code = `
    class PcmCaptureProcessor extends AudioWorkletProcessor {
      constructor() {
        super()
      }

      process(inputs) {
        const input = inputs[0]
        if (input && input[0]) {
          const channel = input[0]
          const copy = new Float32Array(channel.length)
          copy.set(channel)
          this.port.postMessage(copy.buffer, [copy.buffer])
        }
        return true
      }
    }

    registerProcessor('pcm-capture-processor', PcmCaptureProcessor)
  `
  const blob = new Blob([code], { type: 'application/javascript' })
  return URL.createObjectURL(blob)
}

// =============================================================================
// 流式 PCM 音频播放器（24kHz 连续播放）
// =============================================================================

class TranslateAudioPlayer {
  private audioContext: AudioContext | null = null
  private nextPlayTime = 0

  async unlock(): Promise<void> {
    const AC = window.AudioContext || (window as any).webkitAudioContext
    if (!this.audioContext) {
      this.audioContext = new AC({ sampleRate: PLAYBACK_SAMPLE_RATE })
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }
  }

  playChunk(pcmData: ArrayBuffer): void {
    if (!this.audioContext) return

    try {
      const int16Data = new Int16Array(pcmData)
      const audioBuffer = this.audioContext.createBuffer(
        1,
        int16Data.length,
        PLAYBACK_SAMPLE_RATE,
      )
      const channelData = audioBuffer.getChannelData(0)
      for (let i = 0; i < int16Data.length; i++) {
        channelData[i] = int16Data[i] / 32768.0
      }

      const source = this.audioContext.createBufferSource()
      source.buffer = audioBuffer
      source.connect(this.audioContext.destination)

      const now = this.audioContext.currentTime
      if (this.nextPlayTime < now) {
        this.nextPlayTime = now + 0.05
      }

      const startTime = this.nextPlayTime
      source.start(startTime)
      this.nextPlayTime = startTime + audioBuffer.duration
    } catch (error) {
      console.error('[TranslateAudioPlayer] 播放 chunk 失败:', error)
    }
  }

  stop(): void {
    try {
      this.audioContext?.close()
    } catch {
      // ignore
    }
    this.audioContext = null
    this.nextPlayTime = 0
  }
}

// =============================================================================
// 主组件
// =============================================================================

export default function LiveTranslate() {
  const [isRunning, setIsRunning] = useState(false)
  const [targetLang, setTargetLang] = useState('en')
  const [voiceEnabled, setVoiceEnabled] = useState(false)

  // 🌟 使用数组来存储历史记录
  const [inputLines, setInputLines] = useState<TransLine[]>([])
  const [outputLines, setOutputLines] = useState<TransLine[]>([])

  const [status, setStatus] = useState('就绪')
  const [error, setError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const captureCtxRef = useRef<AudioContext | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const captureChunksRef = useRef<Float32Array[]>([])
  const sendIntervalRef = useRef<number | null>(null)
  const playerRef = useRef<TranslateAudioPlayer | null>(null)
  const workletUrlRef = useRef<string | null>(null)
  const isRunningRef = useRef(false)

  const inputContainerRef = useRef<HTMLDivElement>(null)
  const outputContainerRef = useRef<HTMLDivElement>(null)

  // ---------------------------------------------------------------------------
  // 自动滚动到底部
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (inputContainerRef.current) {
      inputContainerRef.current.scrollTop = inputContainerRef.current.scrollHeight
    }
  }, [inputLines])

  useEffect(() => {
    if (outputContainerRef.current) {
      outputContainerRef.current.scrollTop = outputContainerRef.current.scrollHeight
    }
  }, [outputLines])

  // ---------------------------------------------------------------------------
  // 初始化与清理
  // ---------------------------------------------------------------------------

  useEffect(() => {
    playerRef.current = new TranslateAudioPlayer()
    return () => {
      stopTranslation()
      playerRef.current?.stop()
      playerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------------------------------------------------------------------------
  // WebSocket URL 构建
  // ---------------------------------------------------------------------------

  const getTranslateWebSocketUrl = useCallback((lang: string): string => {
    const hostname = window.location.hostname
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
    const query = `?target_lang=${encodeURIComponent(lang)}`

    if (!isLocalhost && window.location.protocol === 'https:') {
      return `wss://${hostname}/ws/translate${query}`
    }

    if (isLocalhost) {
      return `ws://${hostname}:8080/ws/translate${query}`
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${protocol}//${hostname}${window.location.port ? ':' + window.location.port : ''}/ws/translate${query}`
  }, [])

  // ---------------------------------------------------------------------------
  // 智能累加文本算法
  // ---------------------------------------------------------------------------
  const updateLines = useCallback((newText: string, setLines: React.Dispatch<React.SetStateAction<TransLine[]>>) => {
    const now = Date.now()
    setLines((prev) => {
      if (prev.length === 0) {
        return [{ id: Math.random().toString(36).substring(2, 9), text: newText, lastUpdate: now }]
      }

      const last = prev[prev.length - 1]
      const timeDiff = now - last.lastUpdate

      // 检查新文本和旧文本的重合度，忽略大小写和两端空格
      const p = last.text.trim().toLowerCase()
      const n = newText.trim().toLowerCase()

      let isUpdate = false
      // 如果两条消息到达时间差小于 2000 毫秒 (2秒)，且具有包含关系，视为同一句话的累加更新
      if (timeDiff < 2000) {
        if (n.includes(p) || p.includes(n) || n.startsWith(p) || p.startsWith(n)) {
          isUpdate = true
        }
      }

      if (isUpdate) {
        // 替换数组中最后一个元素
        const updated = [...prev]
        updated[updated.length - 1] = { ...last, text: newText, lastUpdate: now }
        return updated
      } else {
        // 作为全新的句子追加
        return [...prev, { id: Math.random().toString(36).substring(2, 9), text: newText, lastUpdate: now }]
      }
    })
  }, [])

  // ---------------------------------------------------------------------------
  // 音频采集
  // ---------------------------------------------------------------------------

  const startAudioCapture = useCallback(async (ws: WebSocket): Promise<void> => {
    let stream: MediaStream | null = null
    let captureCtx: AudioContext | null = null

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
      mediaStreamRef.current = stream

      if (!isRunningRef.current) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }

      const AC = window.AudioContext || (window as any).webkitAudioContext
      captureCtx = new AC({ sampleRate: CAPTURE_SAMPLE_RATE })
      captureCtxRef.current = captureCtx
    } catch (err: any) {
      console.error('[LiveTranslate] 麦克风/AudioContext 初始化失败:', err)
      setError(`麦克风初始化失败: ${err.name || ''} ${err.message}`)
      setStatus('错误')
      stopTranslation()
      return
    }

    try {
      if (!captureCtx.audioWorklet) throw new Error('当前浏览器不支持 AudioWorklet')

      workletUrlRef.current = createCaptureWorkletURL()
      await captureCtx.audioWorklet.addModule(workletUrlRef.current)

      const source = captureCtx.createMediaStreamSource(stream)
      const workletNode = new AudioWorkletNode(captureCtx, 'pcm-capture-processor')
      workletNodeRef.current = workletNode

      const silenceGain = captureCtx.createGain()
      silenceGain.gain.value = 0
      workletNode.connect(silenceGain).connect(captureCtx.destination)
      source.connect(workletNode)

      workletNode.port.onmessage = (event) => {
        const buffer = event.data as ArrayBuffer
        if (buffer && buffer.byteLength > 0) {
          captureChunksRef.current.push(new Float32Array(buffer))
        }
      }
    } catch (err) {
      console.warn('[LiveTranslate] AudioWorklet 不可用，回退到 ScriptProcessorNode:', err)
      try {
        const source = captureCtx.createMediaStreamSource(stream)
        const proc = captureCtx.createScriptProcessor(4096, 1, 1)

        proc.onaudioprocess = (e) => {
          const inputData = e.inputBuffer.getChannelData(0)
          const copy = new Float32Array(inputData.length)
          copy.set(inputData)
          captureChunksRef.current.push(copy)
        }

        const silenceGain = captureCtx.createGain()
        silenceGain.gain.value = 0
        proc.connect(silenceGain).connect(captureCtx.destination)
        source.connect(proc)

      } catch (fallbackErr: any) {
        console.error('[LiveTranslate] 音频采集完全失败:', fallbackErr)
        setError(`音频采集不可用: ${fallbackErr.message}`)
        setStatus('错误')
        stopTranslation()
        return
      }
    }

    if (!isRunningRef.current) {
      captureCtx?.close().catch(() => { })
      stream?.getTracks().forEach((track) => track.stop())
      return
    }

    sendIntervalRef.current = window.setInterval(() => {
      const chunks = captureChunksRef.current
      if (chunks.length === 0 || ws.readyState !== WebSocket.OPEN) return

      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
      const combined = new Float32Array(totalLength)
      let offset = 0
      for (const chunk of chunks) {
        combined.set(chunk, offset)
        offset += chunk.length
      }
      chunks.length = 0

      const actualRate = captureCtx!.sampleRate
      const resampled =
        actualRate === CAPTURE_SAMPLE_RATE
          ? combined
          : resampleFloat32(combined, actualRate, CAPTURE_SAMPLE_RATE)
      const pcmBuffer = floatToInt16PCM(resampled)

      try {
        ws.send(pcmBuffer)
      } catch (err) {
        console.error('[LiveTranslate] 发送音频失败:', err)
      }
    }, CHUNK_INTERVAL_MS)

  }, [])

  // ---------------------------------------------------------------------------
  // WebSocket 消息处理
  // ---------------------------------------------------------------------------

  const handleWebSocketMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data instanceof ArrayBuffer) {
        if (voiceEnabled) {
          playerRef.current?.playChunk(event.data)
        }
        return
      }

      try {
        const message = JSON.parse(event.data) as TranslateWebSocketMessage
        if (message.type === 'transcription') {
          if (message.input_transcription) {
            updateLines(message.input_transcription, setInputLines)
          }
          if (message.output_transcription) {
            updateLines(message.output_transcription, setOutputLines)
          }
        } else if (message.type === 'error') {
          setError(message.message)
          setStatus('错误')
        }
      } catch (err) {
        console.error('[LiveTranslate] JSON 解析失败:', err)
      }
    },
    [updateLines, voiceEnabled],
  )

  // ---------------------------------------------------------------------------
  // 启动 / 停止
  // ---------------------------------------------------------------------------

  const stopTranslation = useCallback(() => {
    isRunningRef.current = false
    setIsRunning(false)
    setStatus('已停止')

    if (sendIntervalRef.current) {
      clearInterval(sendIntervalRef.current)
      sendIntervalRef.current = null
    }

    captureChunksRef.current = []

    workletNodeRef.current?.port.close()
    workletNodeRef.current?.disconnect()
    workletNodeRef.current = null

    try {
      captureCtxRef.current?.close()
    } catch {
      // ignore
    }
    captureCtxRef.current = null

    mediaStreamRef.current?.getTracks().forEach((track) => track.stop())
    mediaStreamRef.current = null

    if (workletUrlRef.current) {
      URL.revokeObjectURL(workletUrlRef.current)
      workletUrlRef.current = null
    }

    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close()
      }
      wsRef.current = null
    }

    playerRef.current?.stop()
    playerRef.current = new TranslateAudioPlayer()
  }, [])

  const startTranslation = useCallback(async () => {
    if (isRunning) return

    setError(null)
    setInputLines([]) // 清空历史
    setOutputLines([]) // 清空历史
    setStatus('准备中...')
    setIsRunning(true)
    isRunningRef.current = true

    try {
      await playerRef.current?.unlock()

      const ws = new WebSocket(getTranslateWebSocketUrl(targetLang))
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws

      setStatus('正在打开麦克风...')
      startAudioCapture(ws)

      ws.onopen = () => {
        if (!isRunningRef.current) return
        setStatus('同传中')
      }

      ws.onmessage = handleWebSocketMessage

      ws.onerror = (_event) => {
        setError('WebSocket 连接错误')
        setStatus('错误')
        stopTranslation()
      }

      ws.onclose = () => {
        if (isRunningRef.current) {
          setStatus('已断开')
          setIsRunning(false)
          isRunningRef.current = false
        }
      }
    } catch (err: any) {
      setError(`启动失败: ${err.message}`)
      setStatus('错误')
      setIsRunning(false)
      isRunningRef.current = false
    }
  }, [isRunning, targetLang, getTranslateWebSocketUrl, startAudioCapture, handleWebSocketMessage, stopTranslation])

  const toggleTranslation = useCallback(async () => {
    if (isRunning) {
      stopTranslation()
    } else {
      await startTranslation()
    }
  }, [isRunning, startTranslation, stopTranslation])

  // ---------------------------------------------------------------------------
  // UI 渲染
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-50 flex flex-col items-center p-4 safe-area-inset-top safe-area-inset-bottom">
      {/* 背景装饰 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-sky-200/40 blur-3xl" />
        <div className="absolute top-1/2 -left-20 w-80 h-80 rounded-full bg-indigo-200/40 blur-3xl" />
        <div className="absolute -bottom-24 right-1/4 w-96 h-96 rounded-full bg-cyan-100/40 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-3xl">
        <div className="backdrop-blur-xl bg-white/70 rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(124,58,237,0.18)] border border-white/60 p-6 sm:p-8">
          {/* 标题区 */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 shadow-lg shadow-sky-300/40 mb-4">
              <span className="text-3xl" role="img" aria-label="AI 同声传译">
                🌐
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-sky-600 via-indigo-500 to-slate-700 bg-clip-text text-transparent tracking-tight">
              AI 同声传译
            </h1>
            <p className="text-slate-500 text-sm mt-2 font-medium">AI 实时翻译 · 边说边译 · 跨语言沟通</p>

            {/* 页面切换 */}
            <div className="mt-3">
              <a
                href="/"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/60 border border-white/80 text-slate-600 text-xs font-medium hover:bg-white hover:shadow-sm transition-all"
              >
                <span>←</span>
                <span>返回首页</span>
              </a>
            </div>
          </div>

          {/* 目标语言选择 */}
          <div className="mb-4 flex items-center justify-center gap-3">
            <label htmlFor="target-lang" className="text-slate-600 text-sm font-medium">
              目标语言
            </label>
            <select
              id="target-lang"
              value={targetLang}
              disabled={isRunning}
              onChange={(e) => setTargetLang(e.target.value)}
              className="px-4 py-2 rounded-xl bg-white/80 border border-indigo-100 text-slate-700 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          {/* 语音输出开关 */}
          <div className="mb-4 flex items-center justify-center gap-3">
            <button
              onClick={() => setVoiceEnabled((prev) => !prev)}
              className={`
                relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-300
                ${voiceEnabled ? 'bg-indigo-500' : 'bg-slate-300'}
              `}
              aria-pressed={voiceEnabled}
              aria-label={voiceEnabled ? '关闭语音输出' : '开启语音输出'}
            >
              <span
                className={`
                  inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200
                  ${voiceEnabled ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
            <span className="text-slate-600 text-sm font-medium">
              {voiceEnabled ? '语音输出已开启' : '语音输出已关闭'}
            </span>
          </div>

          {/* 耳机提示 */}
          {voiceEnabled && (
            <div className="mb-5 px-4 py-2.5 bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-2xl text-sm text-center font-medium flex items-center justify-center gap-2">
              <span>🎧</span>
              <span>建议佩戴耳机，以获得最佳语音体验</span>
            </div>
          )}

          {/* 错误提示 */}
          {error && (
            <div className="mb-5 px-4 py-3 bg-rose-50 border border-rose-100 text-rose-600 rounded-2xl text-sm text-center font-medium">
              {error}
            </div>
          )}

          {/* 巨大的开始/停止按钮 */}
          <div className="flex justify-center mb-6">
            <button
              onClick={toggleTranslation}
              className={`
                relative w-36 h-36 sm:w-40 sm:h-40 rounded-full
                text-white shadow-[0_16px_40px_-10px]
                transition-all duration-200 ease-out
                flex flex-col items-center justify-center
                hover:scale-105 active:scale-95
                ${isRunning
                  ? 'bg-gradient-to-br from-rose-400 to-red-500 shadow-rose-300/50'
                  : 'bg-gradient-to-br from-sky-500 to-indigo-500 shadow-sky-300/50'
                }
              `}
              aria-label={isRunning ? '停止同传' : '开始同传'}
            >
              {isRunning && (
                <span className="absolute inset-0 rounded-full animate-ping bg-white/20" />
              )}
              <span className="text-3xl sm:text-4xl mb-2">{isRunning ? '■' : '●'}</span>
              <span className="text-lg sm:text-xl font-bold">
                {isRunning ? '停止同传' : '开始同传'}
              </span>
              <span className="text-xs opacity-85 mt-1 font-medium">
                {isRunning ? '麦克风常开中' : '点击开始说话'}
              </span>
            </button>
          </div>

          {/* 状态指示 */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 border border-white/80 shadow-sm">
              <span
                className={`w-2 h-2 rounded-full ${isRunning ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'
                  }`}
              />
              <span className="text-slate-500 text-sm font-medium">{status}</span>
            </div>
          </div>

          {/* 🌟 升级后的 原话 / 翻译 历史分栏 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* 识别原话区 */}
            <div className="bg-white/60 rounded-2xl border border-white/80 p-4 shadow-sm flex flex-col">
              <div className="flex items-center gap-2 mb-3 shrink-0">
                <span className="text-lg">🎤</span>
                <span className="text-xs font-bold text-indigo-500 uppercase tracking-wider">
                  识别原话
                </span>
              </div>
              <div
                ref={inputContainerRef}
                className="h-56 overflow-y-auto pr-2 space-y-3 scroll-smooth scrollbar-thin scrollbar-thumb-indigo-200"
              >
                {inputLines.length > 0 ? (
                  inputLines.map((line, idx) => (
                    <p
                      key={line.id}
                      className={`text-base leading-relaxed font-medium transition-colors duration-300 ${idx === inputLines.length - 1 ? 'text-slate-800' : 'text-slate-400'
                        }`}
                    >
                      {line.text}
                    </p>
                  ))
                ) : (
                  <p className="text-slate-400 text-sm italic">等待说话...</p>
                )}
              </div>
            </div>

            {/* 翻译结果区 */}
            <div className="bg-white/60 rounded-2xl border border-white/80 p-4 shadow-sm flex flex-col">
              <div className="flex items-center gap-2 mb-3 shrink-0">
                <span className="text-lg">🔄</span>
                <span className="text-xs font-bold text-purple-500 uppercase tracking-wider">
                  翻译结果
                </span>
              </div>
              <div
                ref={outputContainerRef}
                className="h-56 overflow-y-auto pr-2 space-y-3 scroll-smooth scrollbar-thin scrollbar-thumb-purple-200"
              >
                {outputLines.length > 0 ? (
                  outputLines.map((line, idx) => (
                    <p
                      key={line.id}
                      className={`text-base leading-relaxed font-medium transition-colors duration-300 ${idx === outputLines.length - 1 ? 'text-slate-800' : 'text-slate-400'
                        }`}
                    >
                      {line.text}
                    </p>
                  ))
                ) : (
                  <p className="text-slate-400 text-sm italic">等待翻译...</p>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}